/**
 * Persists the full profile-shortcuts list via
 * `PUT /admin-panel-v2/configurations/profile-shortcuts` (full-list replace).
 *
 * Callers send the complete set the admin loaded / edited — the backend
 * validates ids, uniqueness of `order`, and rejects unknown ids. Returns
 * the saved DTOs so the UI can reseed from what the server actually stored.
 */
import { API_ROUTES } from "../../api/ApiRoutes";
import { AppStrings } from "../../utils/constants/AppStrings";
import { getApiErrorMessage } from "../../utils/ErrorUtils";
import type {
  ProfileShortcutDto,
  ProfileShortcutWriteDto,
  ProfileShortcutsPutRequest,
} from "../../types/profileShortcuts";
import { getPrivateInstance } from "../_privateInstance";
import { extractProfileShortcutsDto } from "./fetchProfileShortcuts";

type ProfileShortcutsEnvelope = {
  success?: boolean;
  message?: string;
  data?: ProfileShortcutDto[] | { shortcuts?: ProfileShortcutDto[] };
  shortcuts?: ProfileShortcutDto[];
};

export const updateProfileShortcutsDto = async (
  shortcuts: ProfileShortcutWriteDto[],
): Promise<ProfileShortcutDto[]> => {
  const payload: ProfileShortcutsPutRequest = { shortcuts };
  let body: ProfileShortcutsEnvelope;
  try {
    const response = await getPrivateInstance().put<ProfileShortcutsEnvelope>(
      API_ROUTES.PUT_PROFILE_SHORTCUTS,
      payload,
    );
    body = response.data;
  } catch (error) {
    throw new Error(
      getApiErrorMessage(error, AppStrings.PAM_FEATURE_QUICK_ACCESS_SAVE_ERROR),
    );
  }

  if (body?.success === false) {
    throw new Error(
      body.message || AppStrings.PAM_FEATURE_QUICK_ACCESS_SAVE_ERROR,
    );
  }

  const saved = extractProfileShortcutsDto(body);
  if (!saved) {
    // Some gateways echo success without a body — treat the request payload
    // as the source of truth by re-mapping write → read shape.
    return shortcuts.map((item) => ({
      id: item.id,
      name: item.name,
      icon: item.icon,
      enabled: item.enabled,
      order: String(item.order),
    }));
  }
  return saved;
};
